import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Search, Disc3, Loader2, ImagePlus, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { SelectionBar } from "@/components/SelectionBar";
import { SelectMenu, BarButton, LassoArea, crossLabel, useSelection, useSharedSelection } from "@/components/Selection";
import { AddSelectionTo, removeWithPreview } from "@/components/SelectionActions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { AlbumGrid } from "@/components/AlbumCard";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { LibrarySwitch, useOwnerView } from "@/components/LibrarySwitch";
import { api, formatApiError } from "@/lib/api";
import { useRestored } from "@/lib/restore";
import { RefreshLibraryButton } from "@/components/RefreshLibraryButton";
import { useAuth } from "@/context/AuthContext";

const LETTERS = ["#", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"];

const SORTS = { recent: "Recently Added", name: "Title (A–Z)", artist: "Artist", year: "Release Year", random: "Random" };
const DEFAULT_ORDER = { recent: "desc", name: "asc", artist: "asc", year: "desc", random: "desc" };
const PAGE = 60;

export default function Albums() {
  const { viewing } = useOwnerView();
  const [missing, setMissing] = useState(0);
  useEffect(() => {
    if (viewing) return;
    api.get("/artwork/missing").then(({ data }) => setMissing(data.missing)).catch(() => {});
  }, [viewing]);
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const sort = params.get("sort") || "recent";
  const order = params.get("order") || DEFAULT_ORDER[sort] || "desc";
  const letter = params.get("letter") || "";
  const urlQ = params.get("q") || "";
  const genre = params.get("genre") || "";
  const [q, setQ] = useState(urlQ);
  const [debounced, setDebounced] = useState(urlQ);
  const [restored, remember] = useRestored();
  const skipLoad = useRef(!!restored);
  const [albums, setAlbums] = useState(restored?.albums || []);
  const [total, setTotal] = useState(restored?.total || 0);
  const [loading, setLoading] = useState(!restored);

  useEffect(() => { const t = setTimeout(() => setDebounced(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { if (urlQ !== debounced) { setQ(urlQ); setDebounced(urlQ); } }, [urlQ]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (debounced === urlQ) return;
    const next = { sort, order };
    if (debounced) next.q = debounced;
    else if (letter) next.letter = letter;
    setParams(next, { replace: true });
  }, [debounced]); // eslint-disable-line react-hooks/exhaustive-deps

  const reqId = useRef(0);
  const load = useCallback(async (offset = 0) => {
    const my = ++reqId.current;
    setLoading(true);
    const { data } = await api.get("/albums", { params: { sort, order, q: debounced, genre, letter: debounced ? "" : letter, limit: PAGE, offset } });
    if (my !== reqId.current) return;
    setAlbums((prev) => (offset ? [...prev, ...data.items] : data.items));
    setTotal(data.total);
    setLoading(false);
  }, [sort, order, debounced, letter, genre]);

  useEffect(() => {
    if (skipLoad.current) { skipLoad.current = false; return; }
    load(0);
  }, [load]);
  useEffect(() => { if (!loading) remember({ albums, total }); }, [albums, total, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch) => {
    const next = { sort, order, ...(urlQ ? { q: urlQ } : {}), ...(genre ? { genre } : {}), ...(letter ? { letter } : {}), ...patch };
    if (!next.genre) delete next.genre;
    if (!next.letter) delete next.letter;
    setParams(next);
  };
  const showRail = (sort === "name" || sort === "artist") && !debounced;
  const albumIds = useMemo(() => albums.map((a) => a.id), [albums]);
  const s = useSelection(albumIds, "albums");
  const otherArtists = [...useSharedSelection("artists").sel];
  const [busy, setBusy] = useState(false);
  const selected = [...s.sel];
  const removeSelected = async () => {
    setBusy(true);
    try {
      const n = s.count;
      const res = await removeWithPreview({ album_ids: selected, artists: otherArtists }, crossLabel(n, "album", otherArtists.length, "artist"));
      if (res) { s.exit(); load(0); }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const findCovers = async () => {
    const n = albums.filter((a) => s.sel.has(a.id) && !a.has_cover).length;
    if (!n) { toast.info("All selected albums already have covers"); return; }
    try {
      await api.post("/artwork/fix-all", { album_ids: selected });
      toast.success(`Looking for covers for ${n} album${n === 1 ? "" : "s"} — see Missing art for progress`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const withoutArt = albums.filter((a) => !a.has_cover).length;

  return (
    <div>
      <LibrarySwitch />
      <PageHeader stack eyebrow={`${total} albums${genre ? ` · ${genre}` : ""}`} title={genre ? genre : sort === "recent" ? "Recently Added" : "Albums"} testid="albums-title">
        {genre && <button onClick={() => update({ genre: "" })} className="flex h-9 items-center gap-1.5 rounded-full border border-zinc-900 bg-zinc-900 px-4 text-xs text-white" data-testid="albums-genre-clear">Genre: {genre} ×</button>}
        <RefreshLibraryButton onDone={() => load()} />
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search albums or artists"
            data-testid="album-search-input" className="h-10 w-60 rounded-full bg-white pl-9" />
        </div>
        <Select value={sort} onValueChange={(v) => update({ sort: v, order: DEFAULT_ORDER[v] })}>
          <SelectTrigger className="h-10 w-44 rounded-full bg-white" data-testid="album-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`album-sort-${k}`}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        {sort !== "random" && (
          <button onClick={() => update({ order: order === "asc" ? "desc" : "asc" })} data-testid="album-order-toggle"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-600 transition-colors hover:text-zinc-900" title={order.toUpperCase()}>
            {order === "asc" ? <ArrowUpNarrowWide size={16} /> : <ArrowDownWideNarrow size={16} />}
          </button>
        )}
        {!viewing && <SelectMenu s={s} noun="album" testid="albums-select" picks={[
          { label: "Select shown albums without art", icon: ImageOff, count: withoutArt, disabled: !withoutArt, testid: "no-art", onClick: () => s.where((a) => !a.has_cover, albums) },
        ]} />}
        {!viewing && missing > 0 && <Link to="/missing-art" data-testid="albums-missing-art-link"
          className="flex h-10 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-4 text-sm text-amber-800 transition-colors hover:bg-amber-100">
          <ImagePlus size={15} />{missing} without art
        </Link>}
      </PageHeader>
      <SelectionBar count={s.count + otherArtists.length} label={`${crossLabel(s.count, "album", otherArtists.length, "artist")} selected`} hidden={s.hidden} noun="album" busy={busy} onRemove={removeSelected} onClear={s.none} onSelectAll={!s.allShown ? s.all : null}>
        <AddSelectionTo body={{ album_ids: selected, artists: otherArtists }} />
        <BarButton onClick={findCovers} icon={ImagePlus} testid="selection-find-covers" title="Search cover art for the selected albums that have none">Find covers</BarButton>
      </SelectionBar>

      {!loading && albums.length === 0 ? (
        <EmptyState icon={Disc3} title={debounced || letter ? "No matches" : viewing ? "Nothing shared yet" : user?.role === "guest" ? "Welcome — let's get you some music" : "Your shelves are empty"}
          text={debounced || letter ? "Try a different search or letter." : viewing ? "The owner's library has no albums yet." : user?.role === "guest"
            ? "Upload your own files, or ask the owner for access to the shared library — it then appears as a second tab above."
            : "Upload audio files or connect your OneDrive music folder to fill your library."}
          action={!debounced && !letter && !viewing && (
            <div className="flex flex-wrap gap-3">
              {user?.permissions?.upload_app !== false && <Link to="/upload" className="rounded-full bg-zinc-900 px-5 py-2 text-sm text-white hover:bg-zinc-800" data-testid="empty-upload-link">Upload music</Link>}
              {user?.role === "guest" && <Link to="/settings" className="rounded-full border border-zinc-300 px-5 py-2 text-sm hover:bg-white" data-testid="empty-shared-link">About shared access</Link>}
            </div>
          )} />
      ) : (
        <div className={showRail ? "flex gap-4" : ""}>
          <LassoArea s={s} className="min-w-0 flex-1" testid="albums-lasso"><AlbumGrid albums={albums} selectMode={s.mode} selected={s.sel} onSelect={s.toggle} /></LassoArea>
          {showRail && (
            <nav aria-label="Jump to letter" className="sticky top-24 hidden max-h-[70vh] flex-col items-center self-start overflow-y-auto rounded-full border border-zinc-200 bg-white px-1 py-2 md:flex" data-testid="albums-letter-rail">
              <button onClick={() => update({ letter: "" })} data-testid="albums-letter-all" className={`h-5 w-6 rounded-full font-mono text-[10px] ${!letter ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"}`}>All</button>
              {LETTERS.map((L) => (
                <button key={L} onClick={() => update({ letter: L })} data-testid={`albums-letter-${L === "#" ? "num" : L}`}
                  className={`h-5 w-6 rounded-full font-mono text-[10px] transition-colors ${letter === L ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"}`}>{L}</button>
              ))}
            </nav>
          )}
        </div>
      )}
      {loading && <div className="flex py-10"><Loader2 className="animate-spin text-zinc-400" /></div>}
      {!loading && albums.length < total && sort !== "random" && (
        <button onClick={() => load(albums.length)} data-testid="albums-load-more"
          className="mt-12 rounded-full border border-zinc-300 bg-white px-6 py-2 text-sm transition-colors hover:bg-zinc-900 hover:text-white">
          Load more
        </button>
      )}
    </div>
  );
}
